import { useState, useEffect, useCallback } from "react";

interface CockpitPreferences {
    expandedCards: Record<string, boolean>;
    columnWidths?: {
        me: number;
        do: number;
        us: number;
    };
    selectedTimeRange?: "day" | "week" | "month" | "year" | "all";
    transactionFilter?: string;
    compactMode?: boolean;
}

const STORAGE_KEY = "cockpit-preferences";
const DEFAULT_PREFERENCES: CockpitPreferences = {
    expandedCards: {},
    selectedTimeRange: "all",
};

/**
 * Hook for managing CockpitV3 user preferences
 * Persists to localStorage for cross-session memory
 *
 * POC STATUS:
 * - Currently using localStorage for simple persistence
 * - All preferences are client-side only
 *
 * FUTURE STATE:
 * - Sync preferences to backend user profile
 * - Support device-specific preferences
 * - Add preference profiles (gaming vs trading vs social)
 * - Implement server-side caching for faster loads
 */
export const useCockpitPreferences = () => {
    const [preferences, setPreferences] = useState<CockpitPreferences>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return { ...DEFAULT_PREFERENCES, ...parsed };
            }
        } catch (error) {
            console.error("Error loading cockpit preferences:", error);
        }
        return DEFAULT_PREFERENCES;
    });

    // Save to localStorage whenever preferences change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
        } catch (error) {
            console.error("Error saving cockpit preferences:", error);
        }
    }, [preferences]);

    // Toggle card expanded/collapsed state
    const toggleCard = useCallback((cardId: string) => {
        setPreferences(prev => ({
            ...prev,
            expandedCards: {
                ...prev.expandedCards,
                [cardId]: !prev.expandedCards[cardId],
            },
        }));
    }, []);

    // Check if a card is expanded (with default value)
    const isExpanded = useCallback((cardId: string, defaultState = true) => {
        return preferences.expandedCards[cardId] ?? defaultState;
    }, [preferences.expandedCards]);

    // Set time range filter
    const setTimeRange = useCallback((range: "day" | "week" | "month" | "year" | "all") => {
        setPreferences(prev => ({
            ...prev,
            selectedTimeRange: range,
        }));
    }, []);

    // Set transaction filter
    const setTransactionFilter = useCallback((filter: string) => {
        setPreferences(prev => ({
            ...prev,
            transactionFilter: filter,
        }));
    }, []);

    // Set column widths (for resizable columns in future)
    const setColumnWidths = useCallback((widths: { me: number; do: number; us: number }) => {
        setPreferences(prev => ({
            ...prev,
            columnWidths: widths,
        }));
    }, []);

    // Toggle compact mode
    const toggleCompactMode = useCallback(() => {
        setPreferences(prev => ({
            ...prev,
            compactMode: !prev.compactMode,
        }));
    }, []);

    // Reset all preferences to defaults
    const resetPreferences = useCallback(() => {
        setPreferences(DEFAULT_PREFERENCES);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    return {
    // Card expansion
        isExpanded,
        toggleCard,
        expandedCards: preferences.expandedCards,

        // Filters
        selectedTimeRange: preferences.selectedTimeRange || "all",
        setTimeRange,
        transactionFilter: preferences.transactionFilter,
        setTransactionFilter,

        // Layout
        columnWidths: preferences.columnWidths,
        setColumnWidths,
        compactMode: preferences.compactMode || false,
        toggleCompactMode,

        // Utils
        resetPreferences,
        preferences,
    };
};